import styled from 'styled-components';
import { UIData, useUIStore } from '../../hooks/stores/uiStore';
import { IconSelectInput } from '../common/baseInputs/IconSelectInput';
import { NumberInput } from '../common/baseInputs/NumberInput';
import { ToggleInput } from '../common/baseInputs/ToggleInput';
import { useScene } from '../../context/sceneContext';

export const SceneButtons = () => {
  const scene = useScene();
  const [UIData, setUIData] = useUIStore<UIData>();

  return (
    <Container
      onMouseEnter={() => UIData.editorMode === 'editing' && UIData.snapToGrid && setUIData({ gridPreview: true })}
      onMouseLeave={() => UIData.editorMode === 'editing' && UIData.snapToGrid && setUIData({ gridPreview: false })}
    >
      <Row>
        <Group>
          <SimulationIndicator $isSimulation={UIData.editorMode === 'simulation'}>
            <span>{UIData.editorMode === 'simulation' ? 'Simulation' : 'Editing'}</span>
            <ToggleInput
              value={UIData.editorMode === 'simulation'}
              onChange={(newValue) =>
                setUIData({ editorMode: newValue ? 'simulation' : 'editing', gridPreview: false })
              }
            />
          </SimulationIndicator>
        </Group>

        <Group>
          <Button onClick={() => scene.zoomOut()}>
            <i className="icon-minus" />
          </Button>
          <ZoomValue onClick={() => scene.resetZoom()}>{`${(UIData.sceneZoom * 100).toFixed(0)}%`}</ZoomValue>
          <Button onClick={() => scene.zoomIn()}>
            <i className="icon-plus" />
          </Button>
        </Group>

        {UIData.editorMode === 'editing' && (
          <>
            <Group>
              <Button onClick={scene.historyUndo} $isDisabled={UIData.historyUndoStack.length <= 1}>
                <i className="icon-undo" />
              </Button>
              <Button onClick={scene.historyRedo} $isDisabled={UIData.historyRedoStack.length < 1}>
                <i className="icon-redo" />
              </Button>
            </Group>

            <Group>
              <IconSelectInput
                value={UIData.snapToGrid ? 'snapToGrid' : UIData.snapToEdge ? 'snapToEdge' : undefined}
                data={[
                  {
                    value: undefined,
                    iconClassName: 'icon-text-none',
                    id: 'icon-snap-none',
                    tooltip: 'No snapping',
                  },
                  {
                    value: 'snapToEdge',
                    iconClassName: 'icon-edge-snap',
                    id: 'icon-snap-elements',
                    tooltip: 'Edge Snapping',
                  },
                  {
                    value: 'snapToGrid',
                    iconClassName: 'icon-grid-snap',
                    id: 'icon-snap-grid',
                    tooltip: 'Grid Snapping',
                  },
                ]}
                onChange={(newValue) => {
                  switch (newValue) {
                    case 'snapToGrid':
                      setUIData({ snapToGrid: true, snapToEdge: false, gridPreview: true });
                      break;
                    case 'snapToEdge':
                      setUIData({ snapToGrid: false, snapToEdge: true, gridPreview: false });
                      break;
                    default:
                      setUIData({ snapToGrid: false, snapToEdge: false, gridPreview: false });
                  }
                }}
                style={{ margin: '0 5px' }}
              />
              {UIData.snapToGrid && (
                <NumberInput
                  label="Size"
                  id="snapping-grid-size-input"
                  tooltip="Snapping Grid Size"
                  value={UIData.snappingGridSize}
                  onChange={(newValue) => setUIData({ snappingGridSize: Number(newValue) })}
                  minValue={10}
                  maxValue={300}
                  draggingStep={5}
                  draggingSensitive={0.1}
                  style={{ width: '70px' }}
                />
              )}
            </Group>

            <Group>
              {UIData.sceneOffset.x !== 0 && UIData.sceneOffset.y !== 0 && (
                <TextButton
                  style={{ width: '110px' }}
                  onClick={() => {
                    scene.resetPosition();
                  }}
                >
                  Reset Position
                </TextButton>
              )}
            </Group>
          </>
        )}
      </Row>
    </Container>
  );
};

const Container = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  width: calc(100% - 310px);

  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
`;

const Group = styled.div`
  display: flex;

  & > *:first-child {
    border-right: none;
    border-top-left-radius: var(--radius-2);
    border-bottom-left-radius: var(--radius-2);
  }
  & > *:last-child {
    border-left: none;
    border-top-right-radius: var(--radius-2);
    border-bottom-right-radius: var(--radius-2);
  }
`;

const Button = styled.button<{ $isDisabled?: boolean }>`
  box-sizing: border-box;
  cursor: pointer;
  width: 30px;
  height: 30px;

  display: flex;
  justify-content: center;
  align-items: center;

  color: var(--main-2);
  background-color: var(--neutral-2);
  border: 1px solid var(--neutral-3);

  transition:
    background-color var(--transition-2),
    color var(--transition-2);
  &:hover {
    background-color: var(--neutral-3);
    color: var(--main-1);
  }

  ${({ $isDisabled }) => $isDisabled && `opacity: 0.5; pointer-events: none;`}
`;

const TextButton = styled.button`
  box-sizing: border-box;
  cursor: pointer;
  height: 30px;
  padding: 0 10px;

  display: flex;
  justify-content: center;
  align-items: center;

  color: var(--main-2);
  background-color: var(--neutral-2);
  border: 1px solid var(--neutral-3);
  border-radius: var(--radius-2);

  transition:
    background-color var(--transition-2),
    color var(--transition-2);
  &:hover {
    background-color: var(--neutral-3);
    color: var(--main-1);
  }

  font-size: 12px;
  font-family: var(--font-1);
`;

const ZoomValue = styled.div`
  box-sizing: border-box;
  cursor: pointer;
  width: 50px;
  height: 30px;

  display: flex;
  justify-content: center;
  align-items: center;

  color: var(--main-2);
  background-color: var(--neutral-2);
  border: 1px solid var(--neutral-3);

  transition:
    background-color var(--transition-2),
    color var(--transition-2);
  &:hover {
    background-color: var(--neutral-3);
    color: var(--main-1);
  }

  font-size: 12px;
  font-family: var(--font-1);
`;

const SimulationIndicator = styled.div<{ $isSimulation?: boolean }>`
  box-sizing: border-box;
  height: 30px;

  display: flex;
  align-items: center;

  color: var(--main-1);
  font-size: 13px;
  font-family: var(--font-1);
  font-weight: 300;

  span {
    height: 25px;
    min-width: 80px;
    text-align: center;
    background-color: var(--neutral-1);
    border-radius: var(--radius-2);
    margin-right: 5px;

    display: flex;
    align-items: center;
    justify-content: center;
  }

  ${({ $isSimulation }) => $isSimulation && `color: var(--primary-1);`}
`;
